import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import { 
  fetchVisualFlows,
  deleteFlow,
  createOrUpdateFlow,
  clearError,
  clearSuccess
} from '../store/slices/visualFlowSlice'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import CreateFlowModal from '../components/(admin)/CreateFlowModal'
import deleteIcon from '../assets/images/delete.png'
import editIcon from '../assets/images/edit.png'
import NotificationModal from '../components/NotificationModal'

const VisualGuideManager = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { flows, loading, error, success, saving } = useSelector((state) => state.visualFlow)
  
  const [isModalOpen, setIsModalOpen] = useState(false)
  
  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  
  // Notification modal state
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  })

  // Get business ID from localStorage or context
  const businessId = localStorage.getItem('businessId') || 'default-business-id'

  useEffect(() => {
    dispatch(fetchVisualFlows())
  }, [dispatch])

  // Show notifications for errors and success
  useEffect(() => {
    if (error) {
      showNotification('error', 'Error', error)
      dispatch(clearError())
    }
    if (success) {
      showNotification('success', 'Success', success)
      dispatch(clearSuccess())
      dispatch(fetchVisualFlows()) // Refresh flows
    }
  }, [error, success, dispatch])

  // Helper functions for notifications
  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    })
  }

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }))
  }

  const handleDelete = (flowId) => {
    const flow = flows.find(f => f._id === flowId)
    if (flow) {
      setItemToDelete(flow)
      setIsDeleteModalOpen(true)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return

    setIsDeleting(true)
    dispatch(deleteFlow(itemToDelete._id)).then(() => {
      setIsDeleteModalOpen(false)
      setItemToDelete(null)
      setIsDeleting(false)
    }).catch(() => {
      setIsDeleting(false)
    })
  }

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false)
    setItemToDelete(null)
  }

  const handleSignOut = async () => {
    try {
      await dispatch(signOut()).unwrap()
      navigate('/signin')
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }

  const handleCreateFlow = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    if (saving) return
    setIsModalOpen(false)
  }

  const handleSaveFlow = async (flowData) => {
    // Create steps array with media files if provided
    const steps = []
    
    // If there are files, create a step for each file
    if (flowData.files && flowData.files.length > 0) {
      flowData.files.forEach((file, index) => {
        const step = {
          title: file.name || `Step ${index + 1}`,
          description: file.description || `Step with ${file.name}`
        }
        if (file.file && file.file instanceof File) {
          step.media = file.file
        } else if (file.media_asset_id) {
          step.media_asset_id = file.media_asset_id
        }
        steps.push(step)
      })
    } else {
      // Create a default step if no files
      steps.push({
        title: 'Default Step',
        description: 'Default step description'
      })
    }

    try {
      await dispatch(createOrUpdateFlow({
        intentKey: flowData.linkedIntents || 'default-intent',
        intent_id: flowData.linkedIntents,
        title: flowData.title,
        description: flowData.description,
        steps: steps
      })).unwrap()
      setIsModalOpen(false)
    } catch (_) {
      // Error toast is shown via Redux error effect; keep modal open
    }
  }

  const handleRowClick = (flow) => {
    // Navigate to flow details using intentKey
    navigate(`/visual-guides/${flow.intent_key}`)
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return '#10B981' // green
      case 'Inactive':
        return '#6B7280' // gray
      case 'Admin':
        return '#3B82F6' // blue
      default:
        return '#6B7280'
    }
  }

  const containerStyle = {
    marginLeft: '280px', // Account for sidebar width
    padding: '40px',
    backgroundColor: colors.background.primary,
    minHeight: '100vh',
    width: 'calc(100vw - 280px)' // Take full width minus sidebar
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '40px'
  }

  const titleStyle = {
    fontSize: '32px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0
  }


  const createButtonStyle = {
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.2s ease'
  }

  const tableContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  }

  const headerRowStyle = {
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB'
  }

  const headerCellStyle = {
    padding: '16px 24px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    borderBottom: '1px solid #E5E7EB'
  }

  const rowStyle = {
    borderBottom: '1px solid #F3F4F6',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const clickableRowStyle = {
    ...rowStyle,
    ':hover': {
      backgroundColor: '#F9FAFB'
    }
  }

  const cellStyle = {
    padding: '16px 24px',
    fontSize: '14px',
    color: colors.text.primary
  }

  const statusStyle = (status) => ({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: getStatusColor(status) + '20',
    color: getStatusColor(status)
  })

  const actionButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '4px',
    color: '#6B7280',
    transition: 'color 0.2s ease'
  }

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'underline',
    cursor: 'pointer'
  }

  // Delete modal styles (matching IntentManagement)
  const deleteModalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001
  }

  const deleteModalStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '12px',
    padding: '32px',
    width: '400px',
    maxWidth: '90vw',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }

  const deleteModalTitleStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: '0 0 16px 0'
  }

  const deleteModalTextStyle = {
    fontSize: '16px',
    color: colors.text.secondary,
    margin: '0 0 24px 0',
    lineHeight: '1.5'
  }

  const deleteModalButtonGroupStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  }

  const deleteCancelButtonStyle = {
    backgroundColor: 'white',
    color: colors.text.primary,
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const getDeleteConfirmButtonStyle = () => ({
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    opacity: isDeleting ? 0.7 : 1
  })

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div>Loading flows...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '40px', color: '#EF4444' }}>
          <div>Error: {error}</div>
          <button 
            onClick={() => dispatch(fetchVisualFlows())}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              backgroundColor: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={containerStyle}>
        {/* Header */}
        <PageTopBar
          title="Visual Guide Manager"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <button style={createButtonStyle} onClick={handleCreateFlow}>
            <span>+</span>
            Create New Flow
          </button>
        </div>

      {/* Table */}
      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead style={headerRowStyle}>
            <tr>
              <th style={headerCellStyle}>Flow Name</th>
              <th style={headerCellStyle}>Linked Intents</th>
              <th style={headerCellStyle}>Steps Count</th>
              <th style={headerCellStyle}>Status</th>
              <th style={headerCellStyle}>Last Modified</th>
              <th style={headerCellStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {flows.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ ...cellStyle, textAlign: 'center', color: '#6B7280' }}>
                  No flows found. Create your first flow to get started.
                </td>
              </tr>
            ) : (
              flows.map((flow, index) => (
                <tr 
                  key={flow._id} 
                  style={rowStyle}
                  onClick={() => handleRowClick(flow)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#F9FAFB'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent'
                  }}
                >
                  <td style={cellStyle}>
                    <div style={{ fontWeight: '600' }}>{flow.title}</div>
                  </td>
                  <td style={cellStyle}>
                    <span style={{ ...linkStyle, cursor: 'default', textDecoration: 'none' }}>
                      {flow.intent_key || '—'}
                    </span>
                  </td>
                  <td style={cellStyle}>
                    {flow.steps ? flow.steps.length : 0}
                  </td>
                  <td style={cellStyle}>
                    <span style={statusStyle('Active')}>
                      Active
                    </span>
                  </td>
                  <td style={cellStyle}>
                    {formatDate(flow.updated_at || flow.created_at)}
                  </td>
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        style={actionButtonStyle}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(flow._id)
                        }}
                        title="Delete"
                      >
                        <img src={deleteIcon} alt="Delete" style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button
                        style={actionButtonStyle}
                        onClick={(e) => {
                          e.stopPropagation()
                          console.log('Edit flow:', flow._id)
                        }}
                        title="Edit"
                      >
                        <img src={editIcon} alt="Edit" style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && itemToDelete && (
        <div style={deleteModalOverlayStyle}>
          <div style={deleteModalStyle}>
            <h2 style={deleteModalTitleStyle}>Delete Flow</h2>
            <p style={deleteModalTextStyle}>
              Are you sure you want to delete <strong>"{itemToDelete.title}"</strong>? 
              This action cannot be undone and will remove all associated data.
            </p>
            <div style={deleteModalButtonGroupStyle}>
              <button 
                style={deleteCancelButtonStyle} 
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                style={getDeleteConfirmButtonStyle()} 
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Flow Modal */}
      <CreateFlowModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveFlow}
        isSaving={saving}
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </div>
  )
}

export default VisualGuideManager
